import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { TeamBApi, ApiError, type Plant } from '@ai-tiaozs/shared';
import { ImageUploader, PrimaryButton } from '@/components/ImageUploader';
import { VoiceInput } from '@/components/VoiceInput';
import { PlantListSection } from '@/components/PlantList';

/**
 * B 队 · ① 添加植物(王晟骅)
 * 拍照 → AI 识别 → 填名字/位置 → 建档 → 下方即时看到我的植物
 */
export function TeamBAddPlant() {
  const [name, setName] = useState('');
  const [species, setSpecies] = useState('');
  const [confidence, setConfidence] = useState<'high' | 'medium' | 'low' | null>(null);
  const [location, setLocation] = useState('');
  const [photo, setPhoto] = useState<string | null>(null);
  const [identifying, setIdentifying] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [plants, setPlants] = useState<Plant[]>([]);
  const [loadingPlants, setLoadingPlants] = useState(true);
  const [lastAddedId, setLastAddedId] = useState<string | null>(null);
  const [uploadKey, setUploadKey] = useState(0);
  const photoRef = useRef<string | null>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const loadPlants = useCallback(async () => {
    setLoadingPlants(true);
    try {
      const { plants: list } = await TeamBApi.listPlants();
      setPlants(list);
    } catch {
      setPlants([]);
    } finally {
      setLoadingPlants(false);
    }
  }, []);

  useEffect(() => {
    void loadPlants();
  }, [loadPlants]);

  const resetForm = () => {
    setName('');
    setSpecies('');
    setConfidence(null);
    setLocation('');
    setPhoto(null);
    photoRef.current = null;
  };

  const handlePhotoChange = useCallback(async (dataUrl: string | null) => {
    setError(null);
    setSuccess(null);
    if (!dataUrl) {
      if (photoRef.current !== null) {
        setName('');
        setSpecies('');
        setConfidence(null);
      }
      photoRef.current = null;
      setPhoto(null);
      return;
    }
    photoRef.current = dataUrl;
    setPhoto(dataUrl);
    setIdentifying(true);
    try {
      const r = await TeamBApi.identifyPlant({ imageBase64: dataUrl });
      setSpecies(r.species);
      setConfidence(r.confidence);
      setName((prev) => prev.trim() || r.species);
    } catch (e) {
      setError(e instanceof ApiError ? `AI 识别失败,可手填名字: ${e.message}` : 'AI 识别失败,可手填');
    } finally {
      setIdentifying(false);
    }
  }, []);

  const submit = async () => {
    if (!name.trim() || !location.trim()) {
      setError('请填写植物名字和摆放位置');
      return;
    }
    if (!photo) {
      setError('请上传一张植物照片(AI 需要识别种类)');
      return;
    }
    setSubmitting(true);
    setError(null);
    setSuccess(null);
    try {
      const p = await TeamBApi.createPlant({
        name: name.trim(),
        species: species.trim() || '未识别',
        location: location.trim(),
        firstPhotoDataUrl: photo,
      });
      resetForm();
      setUploadKey((k) => k + 1);
      setLastAddedId(p.id);
      setSuccess(`已添加「${p.name}」,可在下方查看`);
      await loadPlants();
      listRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    } catch (e) {
      setError(e instanceof ApiError ? e.message : '创建失败');
    } finally {
      setSubmitting(false);
    }
  };

  const confidenceBadge = () => {
    if (!confidence) return null;
    const map = {
      high: { text: 'AI 高置信度识别', cls: 'bg-emerald-100 text-emerald-700' },
      medium: { text: 'AI 中等置信度,可修改', cls: 'bg-amber-100 text-amber-700' },
      low: { text: 'AI 不太确定,请确认', cls: 'bg-orange-100 text-orange-700' },
    };
    const c = map[confidence];
    return <span className={`badge text-[10px] ${c.cls}`}>🤖 {c.text}</span>;
  };

  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-lg font-bold">① 添加新植物</h1>
        <p className="text-xs text-slate-500">拍照 → AI 识别种类 → 填名字和位置</p>
      </header>

      <section className="card space-y-3">
        <div>
          <label className="text-xs text-slate-500">
            ① 拍一张植物照片(必填 · AI 会自动识别种类)
          </label>
          <div className="mt-1">
            <ImageUploader key={uploadKey} label="拍照" onChange={handlePhotoChange} />
          </div>
          {identifying && (
            <p className="mt-1 flex items-center gap-1 text-[11px] text-teamB-primary">
              <Loader2 className="h-3 w-3 animate-spin" />
              AI 正在识别植物种类…
            </p>
          )}
          {confidence && !identifying && (
            <div className="mt-1 flex items-center gap-2">
              {confidenceBadge()}
              {species && <span className="text-[11px] text-slate-500">识别为: {species}</span>}
            </div>
          )}
        </div>
        <div>
          <label className="text-xs text-slate-500">② 植物名字(AI 填的,可改 · 可口述)</label>
          <div className="mt-1">
            <VoiceInput
              value={name}
              onChange={setName}
              placeholder="如:小熊多肉 / 客厅绿萝"
              maxLength={20}
              ariaLabel="植物名字"
            />
          </div>
        </div>
        <div>
          <label className="text-xs text-slate-500">③ 摆放位置(必填 · 可口述)</label>
          <div className="mt-1">
            <VoiceInput
              value={location}
              onChange={setLocation}
              placeholder="客厅窗台 / 阳台 / 卧室书桌"
              maxLength={40}
              ariaLabel="摆放位置"
            />
          </div>
        </div>
        {error && <p className="text-xs text-teamA-danger">{error}</p>}
        {success && (
          <p className="rounded-md bg-emerald-50 px-2 py-1.5 text-xs text-emerald-700">
            ✅ {success}
            {lastAddedId && (
              <>
                {' '}
                <Link to={`/daily?plantId=${lastAddedId}`} className="underline">
                  去拍照记录 →
                </Link>
              </>
            )}
          </p>
        )}
        <PrimaryButton loading={submitting} loadingText="添加中…" onClick={submit}>
          添加植物
        </PrimaryButton>
        <p className="text-[11px] text-slate-400">
          💡 AI 识别基于你拍的照片,不写死任何植物种类 — 绿萝、多肉、栀子花都能识别。
        </p>
      </section>

      <div ref={listRef}>
        <PlantListSection
          plants={plants}
          loading={loadingPlants}
          highlightId={lastAddedId}
          emptyHint="添加成功后,你的植物会出现在这里"
        />
      </div>
    </div>
  );
}
